export type WifiSecurity = "open" | "wep" | "psk" | "sae" | "eap";

export interface ScannedWifiNetwork {
  ssid: string;
  signalStrengthDbm: number;
  security: WifiSecurity;
}

export interface WifiConnection {
  ssid: string;
  signalStrengthDbm: number;
}

export interface WifiMacAddress {
  address: string;
  randomized: boolean;
}

export interface DeviceWifiState {
  scannedNetworks: ScannedWifiNetwork[];
  savedNetworks: string[];
  connection: WifiConnection | null;
  macAddress: WifiMacAddress | null;
}

export interface WifiNetworkOption {
  ssid: string;
  signalStrengthDbm: number | null;
  security: WifiSecurity | null;
}

// Only `dumpsys wifiscanner` appends per-chain detail to the RSSI: "-49(0:-49/1:-64)".
const SCAN_RESULT_ROW =
  /^\s*(?<bssid>[0-9a-f]{2}(?::[0-9a-f]{2}){5})\s+(?<frequencyMhz>\d{3,5})\s+(?<signalStrengthDbm>-?\d+)(?:\([^)]*\))?\s+(?<ageSeconds>\S+)\s+(?<ssidAndFlags>.*)$/i;

const SCAN_RESULTS_HEADER = "Latest scan results";

const SAVED_NETWORK_ROW = /^ID:\s*\d+\s+SSID:\s*"(.*?)"/gm;

// Greedy SSID capture, so an SSID containing a comma still resolves against the single
// "BSSID:" that follows it.
const CURRENT_CONNECTION_ROW = /^mWifiInfo SSID:\s*(.*),\s*BSSID:.*?RSSI:\s*(-?\d+)/m;

const CURRENT_MAC_ROW =
  /^mWifiInfo SSID:.*,\s*BSSID:\s*\S+,\s*MAC:\s*([0-9a-f]{2}(?::[0-9a-f]{2}){5})/im;

// Reported when the radio has no address yet or the caller may not read it.
const PLACEHOLDER_MAC = "02:00:00:00:00:00";

// Android's per-network random MACs set this bit; a burned-in address never does.
const LOCALLY_ADMINISTERED_BIT = 0x02;

const UNKNOWN_SSID = "<unknown ssid>";

const RSSI_WHEN_DISCONNECTED = -127;

export const SECURITY_LABEL_KEYS: Record<WifiSecurity, string> = {
  open: "DeviceWifi.security.open",
  wep: "DeviceWifi.security.wep",
  psk: "DeviceWifi.security.psk",
  sae: "DeviceWifi.security.sae",
  eap: "DeviceWifi.security.eap",
};

export function securityFromFlags(flags: string): WifiSecurity {
  const upperFlags = flags.toUpperCase();
  if (upperFlags.includes("SAE")) return "sae";
  if (upperFlags.includes("EAP")) return "eap";
  if (upperFlags.includes("PSK")) return "psk";
  if (upperFlags.includes("WEP")) return "wep";
  return "open";
}

export function signalBars(signalStrengthDbm: number): 1 | 2 | 3 | 4 {
  if (signalStrengthDbm >= -55) return 4;
  if (signalStrengthDbm >= -66) return 3;
  if (signalStrengthDbm >= -77) return 2;
  return 1;
}

/**
 * The flags are their own column: "[...]" groups written back to back at the end of the row.
 * Walking that run from the right leaves an SSID's own spaces and brackets intact, which
 * splitting on either would not ("Clinic[5G]").
 */
function splitSsidAndFlags(ssidAndFlags: string): { ssid: string; flags: string } {
  const tail = ssidAndFlags.trim();
  let flagsStart = tail.length;

  while (flagsStart > 0 && tail[flagsStart - 1] === "]") {
    const groupStart = tail.lastIndexOf("[", flagsStart - 1);
    if (groupStart === -1) break;

    // Start of the tail means the SSID column was empty (a hidden network).
    const precedingCharacter = groupStart === 0 ? "" : tail[groupStart - 1];
    const startsItsOwnColumn =
      precedingCharacter === "" || precedingCharacter === "]" || /\s/.test(precedingCharacter);
    if (!startsItsOwnColumn) break;

    flagsStart = groupStart;
  }

  return { ssid: tail.slice(0, flagsStart).trim(), flags: tail.slice(flagsStart) };
}

// `dumpsys wifiscanner` wraps the table in pages of state-machine logs.
function scanTableSection(output: string): string {
  const headerIndex = output.indexOf(SCAN_RESULTS_HEADER);
  return headerIndex === -1 ? output : output.slice(headerIndex);
}

export function parseScanResults(output: string): ScannedWifiNetwork[] {
  const strongestBySsid = new Map<string, ScannedWifiNetwork>();

  for (const line of scanTableSection(output).split(/\r?\n/)) {
    const columns = SCAN_RESULT_ROW.exec(line)?.groups;
    if (!columns) continue;

    const signalStrengthDbm = Number(columns.signalStrengthDbm);
    if (!Number.isFinite(signalStrengthDbm)) continue;

    const { ssid, flags } = splitSsidAndFlags(columns.ssidAndFlags);
    if (!ssid || ssid === UNKNOWN_SSID) continue;

    const strongest = strongestBySsid.get(ssid);
    if (strongest && strongest.signalStrengthDbm >= signalStrengthDbm) continue;

    strongestBySsid.set(ssid, { ssid, signalStrengthDbm, security: securityFromFlags(flags) });
  }

  return [...strongestBySsid.values()].sort((a, b) => b.signalStrengthDbm - a.signalStrengthDbm);
}

export function parseSavedNetworks(dumpsysWifiOutput: string): string[] {
  const ssids = [...dumpsysWifiOutput.matchAll(SAVED_NETWORK_ROW)]
    .map((match) => match[1])
    .filter((ssid) => ssid !== "" && ssid !== UNKNOWN_SSID);
  return [...new Set(ssids)];
}

export function parseCurrentConnection(dumpsysWifiOutput: string): WifiConnection | null {
  const match = CURRENT_CONNECTION_ROW.exec(dumpsysWifiOutput);
  if (!match) return null;

  const ssid = match[1].trim();
  const signalStrengthDbm = Number(match[2]);
  if (!ssid || ssid === UNKNOWN_SSID || signalStrengthDbm <= RSSI_WHEN_DISCONNECTED) return null;

  return { ssid, signalStrengthDbm };
}

export function parseWifiMacAddress(dumpsysWifiOutput: string): WifiMacAddress | null {
  const match = CURRENT_MAC_ROW.exec(dumpsysWifiOutput);
  if (!match) return null;

  const address = match[1].toUpperCase();
  if (address === PLACEHOLDER_MAC) return null;

  const firstOctet = parseInt(address.slice(0, 2), 16);
  return { address, randomized: (firstOctet & LOCALLY_ADMINISTERED_BIT) !== 0 };
}

export function networkOptions({
  scannedNetworks,
  savedNetworks,
}: DeviceWifiState): WifiNetworkOption[] {
  const scannedSsids = new Set(scannedNetworks.map((network) => network.ssid));

  return [
    ...scannedNetworks.map(({ ssid, signalStrengthDbm, security }) => ({
      ssid,
      signalStrengthDbm,
      security,
    })),
    ...savedNetworks
      .filter((ssid) => !scannedSsids.has(ssid))
      .map((ssid) => ({ ssid, signalStrengthDbm: null, security: null })),
  ];
}
